import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaCalendar, FaUser, FaFilter } from 'react-icons/fa';
import config from '../../config';
import terms from '../../config/terminology';
import { formatCurrency } from '../../utils/formatCurrency';

export default function CreatorDonationReport() {
  const [donations, setDonations] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [summary, setSummary] = useState({
    total_donations: 0, net_total: 0,
    total_paid_out: 0, remaining_balance: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [activeTab, setActiveTab] = useState('donations');
  const navigate = useNavigate();

  const token = localStorage.getItem('access_token');

  const fetchReport = async () => {
    try {
      setLoading(true);
      let url = `${config.API_URL}/creators/donation-report?`;
      if (dateFrom) url += `&date_from=${dateFrom}`;
      if (dateTo) url += `&date_to=${dateTo}`;

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.status === 401) {
        navigate('/creator-login');
        return;
      }
      if (!res.ok) throw new Error('Failed to fetch report');

      const data = await res.json();
      setDonations(data.donations);
      setPayouts(data.payouts || []);
      setSummary(data.summary);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) {
      navigate('/creator-login');
      return;
    }
    fetchReport();
  }, []);

  const applyFilters = () => {
    fetchReport();
  };

  const clearFilters = () => {
    setDateFrom('');
    setDateTo('');
    setTimeout(() => fetchReport(), 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading && donations.length === 0) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-xl">Loading report...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-red-500">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white p-8">
      <div className="max-w-5xl mx-auto">
        <Link to="/creator-dashboard" className="inline-flex items-center space-x-2 text-gray-400 hover:text-white mb-6 transition">
          <FaArrowLeft />
          <span>Back to Dashboard</span>
        </Link>

        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">{terms.Donation} Report</h1>
          <p className="text-gray-400">Your earnings</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-gray-900 p-4 rounded-lg border border-gray-800">
            <p className="text-gray-400 text-sm">Total {terms.Donations}</p>
            <p className="text-2xl font-bold">{summary.total_donations}</p>
          </div>
          <div className="bg-gray-900 p-4 rounded-lg border border-gray-800">
            <p className="text-gray-400 text-sm">Paid Out</p>
            <p className="text-2xl font-bold text-blue-400">{formatCurrency(summary.total_paid_out)}</p>
          </div>
          <div className="bg-gray-900 p-4 rounded-lg border border-green-500/50">
            <p className="text-gray-400 text-sm">Remaining Balance</p>
            <p className="text-2xl font-bold text-green-400">{formatCurrency(summary.remaining_balance)}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-gray-900 rounded-lg border border-gray-800 p-4 mb-6">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition ${
                showFilters || dateFrom || dateTo ? 'bg-blue-500/20 text-blue-400' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              <FaFilter />
              <span>Date Filter</span>
            </button>
            <button
              onClick={fetchReport}
              className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition"
            >
              Refresh
            </button>
          </div>

          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-800 flex flex-wrap items-end gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">From</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">To</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                />
              </div>
              <button
                onClick={applyFilters}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg transition"
              >
                Apply
              </button>
              {(dateFrom || dateTo) && (
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition"
                >
                  Clear
                </button>
              )}
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex space-x-4 mb-4">
          <button
            onClick={() => setActiveTab('donations')}
            className={`px-4 py-2 rounded-lg transition ${
              activeTab === 'donations'
                ? 'bg-blue-500 text-white'
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
          >
            {terms.Donations} ({donations.length})
          </button>
          <button
            onClick={() => setActiveTab('payouts')}
            className={`px-4 py-2 rounded-lg transition ${
              activeTab === 'payouts'
                ? 'bg-blue-500 text-white'
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
          >
            Payouts ({payouts.length})
          </button>
        </div>

        {/* Donations Table */}
        {activeTab === 'donations' && (
          <div className="bg-gray-900 rounded-lg border border-gray-800 p-6">
            {donations.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No {terms.donations} found
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-800">
                      <th className="text-left p-4">Date</th>
                      <th className="text-left p-4">From</th>
                      <th className="text-left p-4">Message</th>
                      <th className="text-right p-4">You Receive</th>
                    </tr>
                  </thead>
                  <tbody>
                    {donations.map((d) => (
                      <tr key={d.id} className="border-b border-gray-800 hover:bg-gray-800/50">
                        <td className="p-4">
                          <div className="flex items-center space-x-2">
                            <FaCalendar className="text-gray-500" />
                            <span className="text-sm">{formatDate(d.date)}</span>
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center space-x-2">
                            <FaUser className="text-gray-500" />
                            <span>{d.donor_name}</span>
                          </div>
                        </td>
                        <td className="p-4">
                          <span className="text-gray-400 text-sm truncate block max-w-[250px]" title={d.message}>
                            {d.message || '-'}
                          </span>
                        </td>
                        <td className="p-4 text-right">
                          <span className="font-semibold text-green-400">{formatCurrency(d.net_amount)}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Payouts Table */}
        {activeTab === 'payouts' && (
          <div className="bg-gray-900 rounded-lg border border-gray-800 p-6">
            {payouts.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No payouts found
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-800">
                      <th className="text-left p-4">Date</th>
                      <th className="text-left p-4">Description</th>
                      <th className="text-right p-4">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payouts.map((p) => (
                      <tr key={p.id} className="border-b border-gray-800 hover:bg-gray-800/50">
                        <td className="p-4">
                          <div className="flex items-center space-x-2">
                            <FaCalendar className="text-gray-500" />
                            <span className="text-sm">{formatDate(p.date)}</span>
                          </div>
                        </td>
                        <td className="p-4">
                          <span className="text-gray-300">{p.description || 'Payout'}</span>
                        </td>
                        <td className="p-4 text-right">
                          <span className="font-semibold text-blue-400">{formatCurrency(p.amount)}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}